import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { getTranslatedItem } from '@/utils/language.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useGetMatchingEmailTemplateError } from '@/hooks/email-template.hooks';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';
import { Select } from '@/components/Controls/Select';
import { getEmailTemplateTypeOptions } from '@/utils/constant.utils';

export const EmailTemplateInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const translatedEmailTemplate = getTranslatedItem(
    data?.translatedEmailTemplates,
  );

  /**
   * === Set Value Watchers section ===
   */
  const platformTemplateIdValue =
    watch && watch('platformTemplateId' as Path<TSchema>);
  const nameValue = watch && watch('name' as Path<TSchema>);
  const typeValue = watch && watch('type' as Path<TSchema>);
  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: matchingEmailTemplateError } = useGetMatchingEmailTemplateError(
    {
      id: data?.id,
      platformTemplateId: platformTemplateIdValue,
      type: typeValue,
      name: nameValue,
    },
  );

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (platformTemplateIdValue) {
      resetFieldError<TSchema>({ fieldName: 'platformTemplateId', setError });
    }

    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformTemplateIdValue, nameValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue(
        'platformTemplateId' as Path<TSchema>,
        data.platformTemplateId ?? null,
      );
      setValue(
        'name' as Path<TSchema>,
        (translatedEmailTemplate?.name || data.name) ?? '',
      );
      setValue('type' as Path<TSchema>, data.type ?? '');
      setValue('active' as Path<TSchema>, data.active);
    }
  }, [setValue, data, translatedEmailTemplate]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const emailTemplateErrorMessages =
      matchingEmailTemplateError?.data?.errorMessages;

    if (
      emailTemplateErrorMessages?.includes(
        FieldError.TEMPLATE_ID_WITH_TYPE_EXISTS,
      )
    ) {
      setError('platformTemplateId' as Path<TSchema>, {
        message: t('templateIdAlreadyExistsWithThisType'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (
      emailTemplateErrorMessages?.includes(FieldError.NAME_WITH_TYPE_EXISTS)
    ) {
      setError('name' as Path<TSchema>, {
        message: t('templateNameAlreadyExistsWithThisType'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection
            text={t('theBrevoEmailTemplate')}
            defaultIconClassName="ki-badge"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormField
            text={t('brevoTemplateId')}
            required
            textWrapperClassName="mt-3 max-md:mt-0"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register &&
                  register('platformTemplateId' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.platformTemplateId?.message as string)
                }
                placeholder={t('brevoTemplateId')}
                onInput={(e) => {
                  // Enforce to input only a positive number
                  e.currentTarget.value =
                    Number(e.currentTarget.value) > 0
                      ? e.currentTarget.value
                      : null;
                }}
                type="number"
              />
            </div>
          </FormField>
          <FormField
            text={t('templateName')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('name' as Path<TSchema>))}
                errorMessage={errors && (errors.name?.message as string)}
                placeholder={t('templateName')}
                type="text"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:type')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Select
                {...(register && register('type' as Path<TSchema>))}
                errorMessage={errors && (errors.type?.message as string)}
                options={getEmailTemplateTypeOptions(
                  t,
                  t('chooseEmailTemplateType'),
                )}
              />
            </div>
          </FormField>
          <FormField
            text={t('common:activeStatus')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Switch
                {...(register && register('active' as Path<TSchema>))}
                errorMessage={errors && (errors.active?.message as string)}
                checked={data ? activeValue : true}
              />
            </div>
          </FormField>
        </>
      </Card>
    </form>
  );
};
